// pdfExport.js
// PDF export functionality using html2canvas for flyer and jsPDF primitives for card
// Note: Both libraries are loaded dynamically to avoid bundling them for every user

import pairCareIconUrl from '../assets/svgs/PairSelfCareIcon.svg';

// DPI constants for flyer capture (must match ProfileFlyer.jsx)
// TARGET_DPI = 600; // Target resolution for print quality (scale factor: 600/96 = 6.25)
const JPEG_QUALITY = 0.92; // JPEG quality (0-1); 0.92 is visually lossless at 600 DPI

/**
 * Save the flyer as a PDF by capturing the rendered HTML.
 * Document size: Letter (8.5 x 11 inches) portrait
 *
 * @param {Object} provider - The provider object
 * @param {string} publicProfileUrl - The public profile URL
 * @param {Object} config - Config object (unused for canvas-based capture)
 * @param {HTMLCanvasElement} canvas - Canvas from html2canvas capture
 */
export async function saveFlyerPDF(provider, publicProfileUrl, config, canvas) {
  if (!provider || !canvas) {
    throw new Error('Provider and canvas are required.');
  }

  try {
    const { jsPDF } = await import('jspdf');

    const friendlyUrl = provider.friendlyUrl || 'flyer';
    const filename = `${friendlyUrl}-flyer.pdf`;

    // Convert canvas to high-quality JPEG image data URL for better compression
    // JPEG quality 0.92 is visually indistinguishable in print while keeping file size manageable
    const imageData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);

    // Create Letter-portrait PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter'
    });

    const pageWidth = pdf.internal.pageSize.getWidth(); // 8.5"
    const pageHeight = pdf.internal.pageSize.getHeight(); // 11"

    // With the flyer locked to 8.5x11, place the captured image to fill the whole page
    // Use 'JPEG' format to match the image type we created
    pdf.addImage(imageData, 'JPEG', 0, 0, pageWidth, pageHeight);

    // Save the PDF
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating flyer PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}

/**
 * Rasterize an SVG icon to a PNG data URL.
 * @param {string} svgText - SVG markup with currentcolor for stroke/fill
 * @param {string} color - Hex color to replace currentcolor
 * @returns {Promise<string>} PNG data URL
 */
async function rasterizeIconSvg(svgText, color) {
  // Cannot rasterize in Node.js environment (tests, etc.)
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('SVG rasterization not available in this environment');
  }

  try {
    // Replace currentcolor with the specified color
    const coloredSvg = svgText.replace(/currentcolor/gi, color);

    // Create a data URL from the SVG
    // Use btoa() for Base64 encoding; handle Unicode with encodeURIComponent
    const encodedSvg = encodeURIComponent(coloredSvg);
    const svgDataUrl = 'data:image/svg+xml;utf8,' + encodedSvg;

    // Load the SVG into an image and render to canvas
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Create an offscreen canvas and draw the image at high resolution
        const canvas = document.createElement('canvas');
        const pixelSize = 256; // Draw at 256x256 pixels
        canvas.width = pixelSize;
        canvas.height = pixelSize;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, pixelSize, pixelSize);

        // Convert canvas to PNG data URL
        const pngDataUrl = canvas.toDataURL('image/png');
        resolve(pngDataUrl);
      };
      img.onerror = () => {
        reject(new Error('Failed to load SVG image'));
      };
      img.src = svgDataUrl;
    });
  } catch (error) {
    console.error('Error rasterizing icon SVG:', error);
    throw error;
  }
}

/**
 * Save the business card as a PDF using jsPDF primitives.
 * Card size: 3.5 x 2 inches (standard US business card)
 * Defensive: works with missing data.
 *
 * @param {Object} provider - The provider object
 * @param {string} publicProfileUrl - The public profile URL
 * @param {number} selectedThemeIndex - Index into CARD_THEMES
 * @param {Object} cardThemes - Array of theme objects
 * @param {string} qrDataUrl - Pre-rendered QR code as data URL (optional)
 */
export async function saveCardPDF(provider, publicProfileUrl, selectedThemeIndex, cardThemes, qrDataUrl) {
  if (!provider || !cardThemes) return;

  try {
    const { jsPDF } = await import('jspdf');

    const friendlyUrl = provider.friendlyUrl || 'card';
    const filename = `${friendlyUrl}-card.pdf`;

    // Create PDF with custom size (3.5 x 2 inches)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'in',
      format: [3.5, 2]
    });

  const pageWidth = 3.5;
  const pageHeight = 2;
  const margin = 0.15;
  const theme = cardThemes[selectedThemeIndex] || cardThemes[0];

  // Background color
  const bgColor = theme.background.replace('#', '').match(/.{1,2}/g);
  pdf.setFillColor(
    parseInt(bgColor[0], 16),
    parseInt(bgColor[1], 16),
    parseInt(bgColor[2], 16)
  );
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');

  // Text color
  const textColor = theme.text.replace('#', '').match(/.{1,2}/g);
  pdf.setTextColor(
    parseInt(textColor[0], 16),
    parseInt(textColor[1], 16),
    parseInt(textColor[2], 16)
  );

  // Business name
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  pdf.text(provider.name || '', margin, 0.35);

  // Short description (max 2 lines with wrapping)
  let yPos = 0.55;
  if (provider.shortDescription) {
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    const descLines = pdf.splitTextToSize(provider.shortDescription, pageWidth - 2 * margin);
    const limitedDescLines = descLines.slice(0, 2); // Limit to 2 lines
    pdf.text(limitedDescLines, margin, yPos);
    yPos += Math.min(limitedDescLines.length, 2) * 0.18;
  }

  // Contact info
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');

  yPos += 0.05; // Small gap after description
  if (provider.phoneNumber) {
    pdf.text(`Phone: ${provider.phoneNumber}`, margin, yPos);
    yPos += 0.25;
  }

  if (provider.email) {
    pdf.text(`Email: ${provider.email}`, margin, yPos);
    yPos += 0.25;
  }

  // Address
  const address = [provider.address, provider.city, provider.state, provider.postalCode]
    .filter(Boolean)
    .join(', ');
  if (address) {
    const addressLines = pdf.splitTextToSize(address, pageWidth - 2 * margin - 0.15);
    pdf.text(addressLines, margin, yPos);
    yPos += addressLines.length * 0.2;
  }

  // Website URL at bottom with icon
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'italic');
  const urlWithoutIcon = publicProfileUrl; // We'll draw icon separately
  const urlLines = pdf.splitTextToSize(urlWithoutIcon, pageWidth - 2 * margin - 0.35);
  const bottomY = pageHeight - margin - urlLines.length * 0.15;
  const urlY = Math.max(yPos + 0.1, bottomY);

  // Try to draw the icon if available
  let iconDataUrl = null;
  try {
    // Fetch the SVG file from bundled assets
    const svgResponse = await fetch(pairCareIconUrl);
    if (svgResponse.ok) {
      const svgText = await svgResponse.text();
      iconDataUrl = await rasterizeIconSvg(svgText, theme.text);
    }
  } catch (error) {
    console.warn('Could not load icon for PDF:', error.message);
    // Continue without icon if it fails
  }

  // Draw icon if available
  if (iconDataUrl) {
    try {
      const iconSize = 0.12;
      pdf.addImage(iconDataUrl, 'PNG', margin, urlY - 0.08, iconSize, iconSize);
      pdf.text(urlLines, margin + 0.15, urlY);
    } catch (error) {
      console.warn('Could not add icon image to PDF:', error.message);
      // Fallback: draw URL without icon
      pdf.text(urlLines, margin, urlY);
    }
  } else {
    pdf.text(urlLines, margin, urlY);
  }

  // QR Code (if provided)
  if (qrDataUrl) {
    const qrSize = 0.8;
    const qrX = pageWidth - margin - qrSize;
    const qrY = pageHeight / 2 - qrSize / 2;
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
  }

    pdf.save(filename);
  } catch (error) {
    console.error('Error generating card PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}
